import { Lock, KeyRound, FileSignature, Link as LinkIcon, Users, Globe } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, Slab, MockVisual, CTABanner } from '@/components/marketing/primitives.jsx';

const primitives = [
  {
    icon: LinkIcon,
    title: 'Merkle-style hash chains',
    description:
      'Every entity has a per-record integrity chain (SHA-256). Any tamper with a historical record breaks the chain on verification.',
  },
  {
    icon: FileSignature,
    title: 'HMAC-SHA256 decision signatures',
    description:
      'Every reviewer decision — dispatch, confirm, dismiss, revoke — is HMAC-signed and stored alongside the decision record.',
  },
  {
    icon: KeyRound,
    title: 'Device attestation',
    description:
      'Mobile submissions carry a device fingerprint + authenticity score. Tampered or rooted devices drop below the production threshold.',
  },
  {
    icon: Users,
    title: 'RBAC with permission grants',
    description:
      'Roles (citizen, agency_officer, agency_supervisor, ifps_supervisor, firl_admin, veris_admin, hub_admin, judge, inspector_general) with fine-grained permissions per endpoint.',
  },
  {
    icon: Globe,
    title: 'Tenant isolation at the query layer',
    description:
      'Every scoped controller applies agency/institution/jurisdiction constraints in the query, not just the policy. Cross-tenant access is structurally blocked.',
  },
  {
    icon: Lock,
    title: 'Encrypted at rest + in transit',
    description:
      'TLS 1.3 end-to-end. AES-256-GCM at rest. Secrets managed through platform KMS. Keys rotated on schedule and on incident.',
  },
];

export default function Security() {
  return (
    <>
      <Hero
        eyebrow="Security & Integrity"
        title="Verifiable integrity — not a claim, a contract."
        description="NACIS is built around cryptographic integrity primitives. Every privileged action writes to an append-only audit log with hash chaining. Every decision is signed. Every bundle can be re-verified outside NACIS."
        primaryCta={{ to: '/contact', label: 'Discuss our security model' }}
        secondaryCta={{ to: '/privacy', label: 'Privacy principles' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Primitives"
            title="Six cryptographic commitments, enforced in code."
          />
          <div className="mt-12">
            <FeatureGrid items={primitives} columns={3} />
          </div>
        </div>
      </section>

      <Slab
        eyebrow="WORM bundles"
        title="Export any audit slice and verify it outside NACIS."
        description="A WORM (Write-Once-Read-Many) bundle packages an audit slice as canonical JSON with the signed hash chain. Anyone with the bundle and a public-key verifier can confirm integrity — no NACIS code required."
        bullets={[
          'Canonical JSON ensures byte-identical re-serialisation',
          'Public verifier available in Rust, Go, and Python',
          'Bundles include the Merkle root hash and decision signatures',
        ]}
        image={<MockVisual accent="emerald" lines={6} />}
      />

      <Slab
        reverse
        eyebrow="Threat model"
        title="What we defend against — and what we do not claim."
        description="We defend against tamper of historical records, cross-tenant data leakage, and unauthorised privileged action. We do not claim protection against nation-state endpoint compromise of a federated partner's own systems — that is why Safe Entity Attestation and IG oversight are mandatory."
        bullets={[
          'In scope: tamper, replay, cross-tenant leakage, privilege escalation',
          'Out of scope for NACIS core: compromise of a partner\'s own endpoint',
          'Compensating controls: IG oversight, signed charter, rotated credentials',
        ]}
        image={<MockVisual accent="rose" lines={5} />}
      />

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="container-narrow">
          <div className="prose-marketing">
            <SectionHeader
              eyebrow="Responsible disclosure"
              title="Report vulnerabilities safely."
              description="We welcome authorised security research. If you believe you have found a vulnerability in NACIS or any of its subsystems, contact us through the responsible-disclosure path below. Safe-harbour terms apply for good-faith research conducted within the published scope."
            />
            <h2>Scope</h2>
            <ul>
              <li>The NACIS API surface (all subsystems documented here)</li>
              <li>The NACIS client web application</li>
              <li>The NACIS mobile applications (iOS, Android)</li>
              <li>The public OSINT ingestion endpoints and allow-listed source registry</li>
            </ul>
            <h2>Out of scope</h2>
            <ul>
              <li>Federated partner systems (report to the partner directly)</li>
              <li>Denial-of-service testing against production</li>
              <li>Social-engineering of NACIS or partner staff</li>
              <li>Physical-security testing of partner facilities</li>
            </ul>
            <h2>How to report</h2>
            <p>
              Email the security team with a detailed write-up, affected endpoints, reproduction steps, and your
              preferred disclosure timeline. We acknowledge within 3 business days and commit to a remediation
              timeline within 10 business days.
            </p>
          </div>
        </div>
      </section>

      <CTABanner
        title="Want to go deeper on our security posture?"
        description="Attested security teams from participating agencies and partners can request a full review package, including threat model, incident playbooks, and IG scope."
        primaryCta={{ to: '/contact', label: 'Request review package' }}
      />
    </>
  );
}
